<template>
  <component :is="is"
             v-if="isVisible"
             ref="badgeRef"
             class="items-center"
             :class="[sizeClass, colorClass, blockClass, fontClass , hoverClass]">
    <font-awesome-icon v-if="active && !computedIcon" :icon="['far', 'check']" class="h-3.5 w-3.5 pr-1"/>
    <font-awesome-icon v-if="computedIcon" :icon="computedIcon" class="h-3.5 w-3.5 pr-1"/>
    <slot/>
  </component>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import type { BadgeColor, BadgeSize } from "~/components/global/utils/BadgeUtils";
import { iconProp, useIconProp } from "~/components/global/utils/iconPropUtil";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const props = defineProps({
  ...iconProp,

  is: {
    type: String,
    default: 'span'
  },

  size: {
    type: String as PropType<BadgeSize>,
    default: 'xs'
  },
  color: {
    type: String as PropType<BadgeColor>,
    default: 'gray'
  },
  font: {
    type: String,
    default: 'medium',
    required: false
  },
  block: {
    type: Boolean,
    default: false
  },
  square: {
    type: Boolean,
    default: false
  },
  circular: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: false
  },
  noWrap: {
    type: Boolean,
    default: false
  },
  active: {
    type: Boolean,
    default: false,
    required: false
  },
  outlined: {
    type: Boolean,
    default: false,
    required: false
  },
  register: {
    type: Boolean,
    default: true,
    required: false
  },
})

const badgeRef = ref<HTMLDivElement | null>(null);
const instance = getCurrentInstance();
const key = String(instance?.vnode.key)

const isVisible = ref<boolean>(true);

/**
 * Computed properties
 */
const {computedIcon} = useIconProp(props);
const sizeClass = computed(() => {
  if (props.square) {
    return {
      xs: 'rounded text-xs p-1 aspect-1',
      sm: 'rounded text-base p-1 aspect-1',
      md: 'rounded text-base p-1.5 aspect-1',
      lg: 'rounded-lg text-lg p-2 aspect-1',
      xl: 'rounded-lg text-xl p-3 aspect-1'
    }[props.size]
  }
  if (props.circular) {
    return {
      xs: 'rounded-full text-xs aspect-1',
      sm: 'rounded-full text-base p-1 aspect-1',
      md: 'rounded-full text-base p-1.5 aspect-1',
      lg: 'rounded-full text-lg p-2 aspect-1',
      xl: 'rounded-full text-xl p-3 aspect-1'
    }[props.size]
  }
  return {
    xs: 'rounded-md text-xs px-2.5 py-0.5',
    sm: 'rounded-md text-sm px-2.5 py-1',
    md: 'rounded-md text-md px-2.5 py-1',
    lg: 'rounded-md text-lg px-2.5 py-1',
    xl: 'rounded-md text-xl px-2.5 py-1'
  }[props.size]
})
const fontClass = computed(() => {
  return {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold'
  }[props.font]
})
const hoverClass = computed(() => {
  if (!props.clickable) return ''
  return {
    gray: 'hover:bg-black-100',
    red: 'hover:bg-red-100',
    yellow: 'hover:bg-yellow-200 ',
    green: 'hover:bg-green-100 ',
    blue: 'hover:bg-blue-100',
    indigo: 'hover:bg-indigo-100 hover:text-indigo-800',
    purple: 'hover:bg-purple-100 hover:text-purple-800',
    pink: 'hover:bg-pink-100 hover:text-pink-800',
    dark: 'hover:bg-gray-800 hover:text-white',
    ghost: 'hover:bg-transparent hover:text-gray-800 hover:border hover:border-paper-border',
    ghostgray: 'hover:bg-transparent hover:text-text-secondary hover:border hover:border-black-400',
    default: 'bg-text-secondary text-gray-800',
  }[props.color]
})
const colorClass = computed(() => {
  if (props.outlined)
    return {
      gray: 'bg-badge-background-gray border border-badge-border-gray text-black-900',
      red: 'badge-background-transparent border border-red-900 text-red-900',
      yellow: 'border border-yellow-950 text-yellow-950',
      green: 'border border-green-500 text-green-800',
      blue: 'border border-blue-500 text-blue-800',
      indigo: 'border border-indigo-500 text-indigo-800',
      purple: 'border border-purple-500 text-purple-800',
      pink: 'border border-pink-500 text-pink-800',
      dark: 'border border-gray-800 text-white',
      ghost: 'border border-transparent text-gray-800 border border-paper-border',
      ghostgray: 'border border-black-400 text-text-secondary',
      default: 'badge-background-gray border badge-border-gray text-black-900',
    }[props.color]
  return {
    gray: 'bg-black-50 text-text-primary',
    red: 'bg-red-100 text-red-900',
    yellow: 'bg-yellow-200 text-yellow-950',
    green: 'bg-green-50 text-green-800',
    blue: 'bg-blue-100 text-blue-900',
    indigo: 'bg-indigo-100 text-indigo-800',
    purple: 'bg-purple-100 text-purple-800',
    pink: 'bg-pink-100 text-pink-800',
    dark: 'bg-gray-800 text-white',
    ghost: 'bg-transparent text-gray-800 border border-paper-border',
    ghostgray: 'bg-transparent text-text-secondary border border-black-400',
    default: 'bg-text-secondary text-gray-800',
  }[props.color]
})
const blockClass = computed(() => {
  if (props.noWrap) return ''
  return props.block ? 'flex' : 'inline-flex'
})

/**
 * Methods
 */
const setVisibility = (visible: boolean) => {
  isVisible.value = visible;
}

/**
 * Injections
 */
// Register and unregister methods will only exist if the parent component is the BfBadgeGroup component. Which is not always the case.
const registerBadge = inject('registerBadge', () => {
}) as (badge: BfBadgeComponent) => void;
const unregisterBadge = inject('unregisterBadge', () => {
}) as (badge: BfBadgeComponent) => void;

/**
 * Mounts
 */

onMounted(() => {
  if (props.register)
    registerBadge({$el: badgeRef.value, width: badgeRef.value?.clientWidth || 0, key, setVisibility})
})

onBeforeUnmount(() => {
  if (props.register)
    unregisterBadge({$el: badgeRef.value, width: badgeRef.value?.clientWidth || 0, key, setVisibility})
})

/**
 * Exposed methods
 */
export interface BfBadgeComponent {
  $el: HTMLDivElement | null;
  width: number | null;
  key: string | null;
  setVisibility: (visible: boolean) => void;
}
</script>